/**
 * DesignerPickerSheet — bottom sheet for selecting a designer.
 * Used by the assign flow (To Design tab) and the reassign flow (drill-down).
 *
 * Props:
 *   designers   — sorted list [{id, name, username, orderCount}]
 *                 admin's own account should already be pinned first by the caller
 *   onSelect    — called with the chosen designer object; sheet does not auto-close
 *                 (caller decides whether to close after API call succeeds)
 *   onClose     — called when overlay or X button is tapped
 *   title       — header string, defaults to "Assign to..."
 *   excludeId   — optional designer id to hide (used when reassigning to avoid
 *                 showing the current assignee as an option)
 *   onUnassign  — optional callback; when provided, renders an "Unassign" button
 *                 at the top of the list before the designer rows
 */
import React from 'react';
import { X, ChevronRight, UserMinus } from 'lucide-react';

export default function DesignerPickerSheet({
  designers,
  onSelect,
  onClose,
  title = 'Assign to…',
  excludeId,
  onUnassign,
  disabled = false,
}) {
  const visible = excludeId
    ? designers.filter(d => d.id !== excludeId)
    : designers;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} />

      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl pt-4 pb-10"
        data-testid="designer-picker-sheet"
      >
        <div className="w-10 h-1 bg-stone-200 rounded-full mx-auto mb-4" />

        <div className="flex items-center justify-between px-5 mb-3">
          <p className="text-base font-bold text-stone-900">{title}</p>
          <button
            onClick={onClose}
            className="p-1 text-stone-400 hover:text-stone-700 transition-colors"
            data-testid="designer-picker-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-72">
          {onUnassign && (
            <button
              disabled={disabled}
              onClick={onUnassign}
              className="w-full flex items-center gap-3 px-5 py-3.5 text-left border-b border-stone-100 active:bg-red-50 transition-colors"
              data-testid="designer-picker-unassign"
            >
              <div className="flex-1 min-w-0">
                <span className="text-sm font-semibold text-red-600">Unassign</span>
                <span className="ml-1.5 text-[10px] text-red-400">return to To Design</span>
              </div>
              <UserMinus className="h-4 w-4 text-red-400 flex-shrink-0" />
            </button>
          )}
          {visible.length === 0 && !onUnassign ? (
            <p className="px-5 py-6 text-sm text-stone-400 text-center">
              No designers available
            </p>
          ) : visible.length === 0 ? null : (
            visible.map((designer, idx) => (
              <button
                key={designer.id}
                disabled={disabled}
                onClick={() => onSelect(designer)}
                className={`w-full flex items-center gap-3 px-5 py-3.5 text-left active:bg-stone-50 transition-colors ${
                  idx < visible.length - 1 ? 'border-b border-stone-100' : ''
                }`}
                data-testid={`designer-picker-row-${designer.id}`}
              >
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-semibold text-stone-900">
                    {designer.name || designer.username}
                  </span>
                  {designer.isSelf && (
                    <span className="ml-1.5 text-[10px] font-medium text-stone-400">
                      (you)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 text-stone-400">
                  {designer.orderCount != null && (
                    <span className="text-xs">{designer.orderCount} assigned</span>
                  )}
                  <ChevronRight className="h-4 w-4 text-stone-300" />
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </>
  );
}
